import { Cancel, CheckCircle, CircleOutlined, PauseCircle, SvgIconComponent, Verified } from "@mui/icons-material";

export * from "./links";
export * from "./paths";

export const DEFAULT_TITLE = "CODE";
export const DEFAULT_DRAWER_WIDTH = 525;

export enum SearchFilter {
  All = "all",
  Teams = "teams",
  Roles = "roles",
  People = "people",
}

export enum SearchResultType {
  Team,
  Role,
  User,
}

export enum SearchResultChipType {
  Team,
  Role,
  User,
}

export enum ConnectObjectType {
  Team = "Team",
  Role = "Role",
  PrimaryRole = "Primary Role",
  Person = "Person",
  Metric = "Critical Metric",
}

export enum TeamTab {
  Overview = "Overview",
  StrategicMap = "Strategic Map",
  Implementation = "Implementation",
  Meetings = "Meetings",
  Metrics = "Metrics",
  Resources = "Resources",
}

export enum MeetingType {
  OneOnOne = "OneOnOne",
  Team = "Team",
}

export enum UserMeetingsFilterByOption {
  WithLead = "WithLead",
  AsLead = "AsLead",
}

export enum MeetingAgendaItemType {
  Blank = 0,
  UserStrategicOwnership = 1,
  UserTasks = 2,
  UserRoles = 3,
  UserLoops = 12,
  UserFeedback = 13,
  TeamStrategy = 4,
  TeamTasks = 5,
  TeamLoops = 6,
  TeamStructureReview = 7,
  TeamMetrics = 8,
  Notepad = 9,
  TeamCoherenceReview = 10,
  Resources = 11,
}

export interface IAgendaType {
  id: number;
  name: string;
  meetingTypeFilter: (type: MeetingType) => boolean;
}

export const MEETING_AGENDA_TYPES: IAgendaType[] = [
  { id: MeetingAgendaItemType.Blank, name: "Blank", meetingTypeFilter: () => true },
  {
    id: MeetingAgendaItemType.UserStrategicOwnership,
    name: "Strategic Ownership",
    meetingTypeFilter: (type) => type === MeetingType.OneOnOne,
  },
  {
    id: MeetingAgendaItemType.UserTasks,
    name: "Tasks",
    meetingTypeFilter: (type) => type === MeetingType.OneOnOne,
  },
  {
    id: MeetingAgendaItemType.UserRoles,
    name: "Roles",
    meetingTypeFilter: (type) => type === MeetingType.OneOnOne,
  },
  {
    id: MeetingAgendaItemType.UserLoops,
    name: "Loops",
    meetingTypeFilter: (type) => type === MeetingType.OneOnOne,
  },
  {
    id: MeetingAgendaItemType.TeamStrategy,
    name: "Team Strategy",
    meetingTypeFilter: (type) => type === MeetingType.Team,
  },
  { id: MeetingAgendaItemType.TeamTasks, name: "Team Tasks", meetingTypeFilter: (type) => type === MeetingType.Team },
  { id: MeetingAgendaItemType.TeamLoops, name: "Team Loops", meetingTypeFilter: (type) => type === MeetingType.Team },
  {
    id: MeetingAgendaItemType.TeamStructureReview,
    name: "Team Structure Review",
    meetingTypeFilter: (type) => type === MeetingType.Team,
  },
  {
    id: MeetingAgendaItemType.TeamMetrics,
    name: "Team Metrics",
    meetingTypeFilter: (type) => type === MeetingType.Team,
  },
  {
    id: MeetingAgendaItemType.Notepad,
    name: "Notepad",
    meetingTypeFilter: () => true,
  },
  {
    id: MeetingAgendaItemType.TeamCoherenceReview,
    name: "Coherence Review",
    meetingTypeFilter: (type) => type === MeetingType.Team,
  },
  {
    id: MeetingAgendaItemType.Resources,
    name: "Embedded Content",
    meetingTypeFilter: () => true,
  },
];

export enum UserTab {
  Overview = "Overview",
  Strategy = "Strategy",
  Implementation = "Implementation",
  Meetings = "Meetings",
  Feedback = "Feedback",
  Metrics = "Metrics",
}

export enum SurveyTab {
  Questions = "Questions",
  Responses = "Responses",
  Actions = "Follow up actions",
}

export const GRAPH_COLORS = [
  "#124143",
  "#1E6131",
  "#198590",
  "#33C1E0",
  "#DADEE0",
  "#F4D172",
  "#F98E15",
  "#7D1911",
  "#4A0F0A",
];

export enum SystemStatus {
  "Created" = 1,
  "Edited" = 2,
  "Deleted" = 3,
}

export enum ActionItemStatus {
  "OnTrack" = 1,
  "AtRisk" = 2,
  "OffTrack" = 3,
  "Completed" = 4,
  "Open" = 6,
  "Paused" = 7,
  "Cancelled" = 8,
}

export enum IssueStatus {
  "Open" = 1,
  "Paused" = 2,
  "Processed" = 3,
  "InProgress" = 4,
}

export enum TaskItemStatus {
  "OnTrack" = 1,
  "AtRisk" = 2,
  "OffTrack" = 3,
  "Completed" = 4,
  "Open" = 6,
  "Paused" = 7,
  "Cancelled" = 8,
}

export enum StatusGroupSelectionState {
  All = "all",
  Partial = "partial",
  None = "none",
}

export enum ActionItemPropertyType {
  Status = "status",
  Ownership = "ownership",
  Notes = "notes",
  Metrics = "metrics",
  DueDate = "dueDate",
  SupportingItems = "supportingItems",
  AzureDevOps = "azureDevOps",
}

export enum FormType {
  PerformanceFeedback = 1,
  PeerFeedback = 2,
  SelfReflection = 3,
}

export enum FormSubmissionPropertyType {
  ReviewedInOneOnOne = "reviewedInOneOnOne",
}

export enum FormSubmissionReviewerType {
  Recipient = 1,
  PeopleLead = 2,
  PrivilegedUser = 3,
}

export enum FormRecipientType {
  Self = 1,
  DirectReportsForPeopleLead = 2,
  AnyPerson = 3,
}

export enum FormSubmissionPrivacyType {
  Public = 1,
  RecipientAndPeopleLead = 2,
  RecipientOnly = 3,
}

export enum TaskItemContextType {
  StrategicItem = "strategicItem",
  Task = "task",
  Issue = "issue",
}

export enum TaskItemPropertyType {
  Status = "status",
  Ownership = "ownership",
  Notes = "notes",
  DueDate = "dueDate",
  Subtasks = "subtasks",
}

export enum SupportingItemType {
  ActionItem = "actionItem",
  TaskItem = "taskItem",
  Issue = "issue",
}

export interface IStatus {
  id: number;
  name: string;
  color: string;
  icon?: SvgIconComponent;
  sortOrder: number;
}

export enum CommentSubject {
  "Comment" = 1,
  "Team" = 2,
  "User" = 3,
  "Role" = 4,
  "Metric" = 13,
  "ActionItem" = 14,
  "Issue" = 15,
  "WikiPage" = 16,
  "TaskItem" = 17,
}

export enum TagType {
  Role = "role",
  SurveyQuestion = "surveyQuestion",
  Metric = "metric",
}

export const ACTION_ITEM_STATUSES: IStatus[] = [
  { id: ActionItemStatus.Open, name: "Open", color: "#818181", icon: CircleOutlined, sortOrder: 1 },
  { id: ActionItemStatus.OnTrack, name: "On Track", color: "#45A324", sortOrder: 2 },
  { id: ActionItemStatus.AtRisk, name: "At Risk", color: "#F2994A", sortOrder: 3 },
  { id: ActionItemStatus.OffTrack, name: "Off Track", color: "#E02626", sortOrder: 4 },
  { id: ActionItemStatus.Paused, name: "Paused", color: "#818181", icon: PauseCircle, sortOrder: 5 },
  { id: ActionItemStatus.Completed, name: "Completed", color: "#45A324", icon: CheckCircle, sortOrder: 6 },
  { id: ActionItemStatus.Cancelled, name: "Cancelled", color: "#818181", icon: Cancel, sortOrder: 7 },
];

export const ISSUE_STATUSES: IStatus[] = [
  { id: IssueStatus.Open, name: "Open", color: "#818181", icon: CircleOutlined, sortOrder: 1 },
  { id: IssueStatus.InProgress, name: "In Progress", color: "#45A324", sortOrder: 2 },
  { id: IssueStatus.Paused, name: "Paused", color: "#818181", icon: PauseCircle, sortOrder: 3 },
  { id: IssueStatus.Processed, name: "Processed", color: "#198590", icon: Verified, sortOrder: 4 },
];

export const TASK_ITEM_STATUSES: IStatus[] = [
  { id: TaskItemStatus.Open, name: "Open", color: "#818181", icon: CircleOutlined, sortOrder: 1 },
  { id: TaskItemStatus.OnTrack, name: "On Track", color: "#45A324", sortOrder: 2 },
  { id: TaskItemStatus.AtRisk, name: "At Risk", color: "#F2994A", sortOrder: 3 },
  { id: TaskItemStatus.OffTrack, name: "Off Track", color: "#E02626", sortOrder: 4 },
  { id: TaskItemStatus.Paused, name: "Paused", color: "#818181", icon: PauseCircle, sortOrder: 5 },
  { id: TaskItemStatus.Completed, name: "Completed", color: "#45A324", icon: CheckCircle, sortOrder: 6 },
  { id: TaskItemStatus.Cancelled, name: "Cancelled", color: "#818181", icon: Cancel, sortOrder: 7 },
];

export const DEFAULT_ACTION_ITEM_STATUS = ActionItemStatus.Open;
export const DEFAULT_ISSUE_STATUS = IssueStatus.Open;
export const DEFAULT_TASK_ITEM_STATUS = TaskItemStatus.Open;

export const ActiveActionItemStatuses = [
  ActionItemStatus.Open,
  ActionItemStatus.OnTrack,
  ActionItemStatus.AtRisk,
  ActionItemStatus.OffTrack,
  ActionItemStatus.Paused,
];
export const ClosedActionItemStatuses = [ActionItemStatus.Completed, ActionItemStatus.Cancelled];

export const ActiveIssueStatuses = [IssueStatus.Open, IssueStatus.InProgress, IssueStatus.Paused];
export const ClosedIssueStatuses = [IssueStatus.Processed];

export const ActiveTaskItemStatuses = [
  TaskItemStatus.Open,
  TaskItemStatus.OnTrack,
  TaskItemStatus.AtRisk,
  TaskItemStatus.OffTrack,
  TaskItemStatus.Paused,
];
export const ClosedTaskItemStatuses = [TaskItemStatus.Completed, TaskItemStatus.Cancelled];

export enum QuestionType {
  Text = 1,
  LinearScale = 2,
  TitleAndDescription = 3,
}

export enum FeedItemType {
  PropertyChange = 1,
  Comment = 2,
}

export enum FeedItemProperty {
  ActionItemStatus = "actionItemStatus",
  Context = "context",
  DecisionMaker = "decisionMaker",
  Description = "description",
  DueDate = "dueDate",
  IssueCollaborators = "issueCollaborators",
  IssueStatus = "issueStatus",
  Note = "note",
  MetricDatapoint = "metricDatapoint",
  MetricDataset = "metricDataset",
  MetricDescription = "metricDescription",
  MetricTitle = "metricTitle",
  Ownership = "ownership",
  Proposal = "proposal",
  RoleName = "roleName",
  RoleDescription = "roleDescription",
  RoleTeam = "roleTeam",
  RoleAccountability = "roleAccountability",
  RoleUser = "roleUser",
  SelectedProposal = "selectedProposal",
  Tags = "tags",
  TaskItemParent = "taskItemParent",
  TaskItemStatus = "taskItemStatus",
  TeamName = "teamName",
  TeamParent = "teamParent",
  TeamPurpose = "teamPurpose",
  TeamSlug = "teamSlug",
}

export enum InAppNotificationType {
  ActionItemStatusChanged = 1,
  TeamEntityComment = 2,
  RoleEntityComment = 3,
  ActionItemComment = 4,
  MetricEntityComment = 5,
  UserEntityComment = 6,
  IssueEntityComment = 7,
  TaskItemEntityComment = 8,
  TaskItemStatusChanged = 9,
  IssueStatusChanged = 10,
  TaskItemCreated = 11,
  IssueCreated = 12,
  TaskItemOwnershipChanged = 13,
  TaskItemNoteChanged = 14,
  TaskItemDueDateChanged = 15,
  ActionItemOwnershipChanged = 16,
  IssueDecisionMakerChanged = 17,
}

export const ActionItemNotificationTypes = [
  InAppNotificationType.ActionItemComment,
  InAppNotificationType.ActionItemStatusChanged,
];

export const IssueNotificationTypes = [
  InAppNotificationType.IssueEntityComment,
  InAppNotificationType.IssueStatusChanged,
  InAppNotificationType.IssueCreated,
  InAppNotificationType.IssueDecisionMakerChanged,
];

export const TaskItemNotificationTypes = [
  InAppNotificationType.TaskItemCreated,
  InAppNotificationType.TaskItemEntityComment,
  InAppNotificationType.TaskItemStatusChanged,
  InAppNotificationType.TaskItemOwnershipChanged,
  InAppNotificationType.TaskItemNoteChanged,
  InAppNotificationType.TaskItemDueDateChanged,
];

export enum IssuePrivacyType {
  Public = 1,
  Limited = 2,
}

export enum TaskItemPrivacyType {
  Public = 1,
  Limited = 2,
}

export enum TeamOverviewSection {
  Feed,
  About,
  Network,
}

export enum TeamNetworkListObjectType {
  Team = 2,
  Role = 1,
  Member = 0,
}

export enum TeamTaskItemsGroupByOption {
  None,
  DueDate,
  InFocus,
  Owner,
  PrimaryOwner,
  Status,
}

export enum UserTaskItemsGroupByOption {
  None,
  DueDate,
  InFocus,
  Owner,
  PrimaryOwner,
  Status,
  Team,
}

export enum UserFeedbackGroupByOption {
  None,
  Type,
  Sender,
  State,
}

export enum UserFeedbackFilterByOption {
  FormName = "formName",
  Sender = "createdByUserId",
  Receiver = "recipientUserId",
  RequiresReviewInOneOnOne = "requiresReviewInOneOnOne",
  ReviewedInOneOnOne = "reviewedInOneOnOne",
  ExcludeDrafts = "excludeDrafts",
}

export enum TaskItemsFilterByOption {
  All,
  Status,
}

export enum TeamIssuesGroupByOption {
  None,
  CreatedBy,
  Status,
}

export enum UserIssuesGroupByOption {
  None,
  Status,
  Team,
}

export enum UserStrategyGroupByOption {
  None,
  DueDate,
  Status,
  Team,
}

export enum UserStrategyFilterByOption {
  All,
  Status = "status",
}

export enum IssuesFilterByOption {
  All,
  Status,
}

export enum TeamStrategyFilterByOption {
  Status = "Status",
  Owner = "Owner",
}

export enum MetricsFilterByOption {
  Team = "Team",
  ReportingPerson = "Reporting Person",
  Tag = "Tag",
}

export enum TaskItemsSortByOption {
  DateCreated = "createdDateTimeUtc",
  DateModified = "lastModifiedDateTimeUtc",
  Name = "description",
  Owners = "owners",
}

export enum IssuesSortByOption {
  Upvotes = "upvotes",
  DateCreated = "createdDateTimeUtc",
  DateModified = "lastModifiedDateTimeUtc",
  Name = "description",
}

export enum UserStrategySortByOption {
  DateCreated = "createdDateTimeUtc",
  DateModified = "lastModifiedDateTimeUtc",
}

export enum SortByDirection {
  Ascending = "asc",
  Descending = "desc",
}

export enum UserFeedbackTab {
  Received = "Received",
  Given = "Given",
}

export enum MeetingNotesTab {
  Shared = "Shared",
  Private = "Private",
}
